import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHomeownerAuth, formatOtpError } from "./HomeownerAuthContext";
import Logo from "../components/Logo";

export default function HomeownerLogin() {
  const navigate = useNavigate();
  const { session, requestOtp, verifyOtp } = useHomeownerAuth();

  const [step, setStep] = useState("email"); // 'email' | 'otp'
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (session && session.submission_id) {
      navigate(`/portal/${session.submission_id}`, { replace: true });
    }
  }, [session, navigate]);

  const submitEmail = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await requestOtp(email.trim().toLowerCase());
      setNotice("If that email is linked to a Folk project, a 6-digit code is on its way.");
      setStep("otp");
    } catch (err) {
      setError(formatOtpError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await verifyOtp(email.trim().toLowerCase(), code.trim());
      navigate(`/portal/${data.submission_id}`, { replace: true });
    } catch (err) {
      setError(formatOtpError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#FFFBC9", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 440 }}>
        <Logo variant="split" size={20} style={{ marginBottom: 40 }} />
        <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: "40px 32px" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600, marginBottom: 6 }}>
            Homeowner sign in
          </div>
          <h1 className="folk-h2" style={{ marginBottom: 6 }} data-testid="homeowner-login-title">
            {step === "email" ? "Welcome back." : "Check your inbox."}
          </h1>
          <p className="folk-body" style={{ fontSize: 15, color: "var(--folk-muted)", marginBottom: 28 }}>
            {step === "email"
              ? "Enter the email you used on your Folk survey. We'll send a one-time code."
              : `We sent a 6-digit code to ${email}. It expires in 10 minutes.`}
          </p>

          {step === "email" && (
            <form onSubmit={submitEmail}>
              <label className="folk-tiny" htmlFor="ho-email">Email</label>
              <input
                id="ho-email"
                type="email"
                className="folk-input"
                data-testid="homeowner-email-input"
                placeholder="you@yourhouse.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              <button
                type="submit"
                className="folk-pill folk-pill-lg"
                data-testid="homeowner-send-code-btn"
                disabled={busy || !email}
                style={{ width: "100%", marginTop: 20 }}
              >
                {busy ? "Sending…" : "Email me a code"}
              </button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={submitCode}>
              <label className="folk-tiny" htmlFor="ho-code">6-digit code</label>
              <input
                id="ho-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                className="folk-input"
                data-testid="homeowner-otp-input"
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                required
                autoFocus
                style={{ letterSpacing: "0.4em", fontSize: 22, textAlign: "center" }}
              />
              <button
                type="submit"
                className="folk-pill folk-pill-lg"
                data-testid="homeowner-verify-btn"
                disabled={busy || code.length !== 6}
                style={{ width: "100%", marginTop: 20 }}
              >
                {busy ? "Verifying…" : "Sign in"}
              </button>
              <button
                type="button"
                data-testid="homeowner-change-email-btn"
                onClick={() => { setStep("email"); setCode(""); setError(""); setNotice(""); }}
                style={{ background: "none", border: "none", color: "var(--folk-muted)", cursor: "pointer", fontFamily: "Host Grotesk, sans-serif", fontSize: 14, marginTop: 14, textDecoration: "underline" }}
              >
                Use a different email
              </button>
            </form>
          )}

          {notice && !error && (
            <div data-testid="homeowner-login-notice" style={{ marginTop: 16, background: "#BADDF0", borderRadius: 12, padding: 12, fontSize: 13, color: "#0F3A54" }}>{notice}</div>
          )}
          {error && (
            <div data-testid="homeowner-login-error" style={{ marginTop: 16, background: "rgba(181, 96, 63, 0.12)", borderRadius: 12, padding: 12, fontSize: 13, color: "#B5603F" }}>{error}</div>
          )}
        </div>
      </div>
    </div>
  );
}
